import { LoaderCircle, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { CopilotoProvider } from "@/features/copiloto/CopilotoContext";
import {
  AppShellContext,
  type AppShellContextValue,
  type OpenMentionOptions
} from "@/shell/app-shell-context";
import { LoginScreen } from "@/features/auth/LoginScreen";
import { MentionDrawer } from "@/features/mentions/MentionDrawer";
import { MentionFilterProvider } from "@/features/mentions/mention-filter-store";
import { revokeThenEndSession } from "@/shell/logout";
import {
  API_URL,
  apiClient,
  type UserProfile
} from "@/shared/api/api-client";
import {
  devAuthEnabled,
  devUserId,
  errorMessage,
  onAccessRevoked,
  onCompanyDisabled,
  setActiveCompanyId
} from "@/shared/api/http";
import {
  beginLogin,
  completeLogin,
  consumeReturnPath,
  consumeSwitchAccount,
  forgetLoginHint,
  forgetSilentLoginAttempt,
  isAuthorizationResponse,
  isSilentLoginRefusal,
  markSilentLoginTried,
  oidcEnabled,
  readIdentityClaims,
  rememberLoginHint,
  rememberedLoginHint,
  silentLoginAlreadyTried,
  switchAccount
} from "@/shared/auth/oidc";
import {
  clearSession,
  currentTokens,
  onSessionLost,
  setSession,
  subscribeToSession
} from "@/shared/auth/session";
import type { Mention } from "@/shared/types/mention";
import { loadPolarityDomain } from "@/shared/types/polarity";
import { Sidebar } from "@/shell/Sidebar";
import { Topbar } from "@/shell/Topbar";
import { backTargetForPath, pageTitleForPath } from "@/shell/navigation";
import { applyColorMode, followSystemColorMode, readColorMode } from "@/shell/color-mode";
import { applyTheme, readThemeId } from "@/shell/theme";
import { KeyboardShortcuts } from "@/shell/KeyboardShortcuts";
import { canOperate, isMaster } from "@/shared/utils/roles";
import { activeCompanies, COMPANY_DISABLED_NOTICE } from "@/shared/utils/companies";
import { retryPauseFrom, usePolling } from "@/shared/hooks/usePolling";

/** Bem mais lento que o polling das telas: aqui é só o número ao lado
 *  de "Menções" no menu, e ele roda em QUALQUER rota de empresa. */
const MENTION_COUNT_INTERVAL_MS = 15000;

// `ACCESS_TOKEN_KEY`/`REFRESH_TOKEN_KEY` saíram em 2026-09-01: token
// não mora mais no `localStorage` (pendência 5). Quem os guarda agora é
// `shared/auth/session.ts`, em memória, e quem sustenta a sessão entre
// recarregamentos é o cookie de SSO do Keycloak.
const SIDEBAR_EXPANDED_KEY = "reputaition.sidebar_expanded";

/**
 * Login por pessoa (2026-10-07). Com o modo ligado, o padrão é ir direto
 * ao Google (`kc_idp_hint`); quem é exceção e clicou em "usuário e senha"
 * fica lembrado NESTE navegador, senão todo carregamento o mandaria ao
 * Google de novo. É conveniência, não segurança: quem decide se o login
 * vale é a API, pela conta.
 */
const PASSWORD_PREFERENCE_KEY = "reputaition.login_method";

function prefersPassword(): boolean {
  try {
    return window.localStorage.getItem(PASSWORD_PREFERENCE_KEY) === "password";
  } catch {
    return false;
  }
}

function rememberPasswordPreference(password: boolean): void {
  try {
    if (password) window.localStorage.setItem(PASSWORD_PREFERENCE_KEY, "password");
    else window.localStorage.removeItem(PASSWORD_PREFERENCE_KEY);
  } catch {
    // Sem storage, a pessoa só escolhe de novo na tela.
  }
}

/** O modo de login da instalação — `null` se a API não responder, que é
 *  o comportamento de antes (sem dica de provedor). */
async function fetchLoginMode(): Promise<string | null> {
  try {
    return (await apiClient.loginOptions()).provider ?? null;
  } catch {
    return null;
  }
}

/**
 * A foto de perfil como endereço `blob:`, ou nulo. Falhar ao baixar não
 * derruba a casca: a tela mostra as iniciais, como sem foto.
 */
async function loadAvatar(
  version: string | null,
  cache: { current: { version: string; url: string } | null }
): Promise<string | null> {
  if (!version) {
    if (cache.current) URL.revokeObjectURL(cache.current.url);
    cache.current = null;
    return null;
  }
  if (cache.current?.version === version) return cache.current.url;
  try {
    const url = URL.createObjectURL(await apiClient.getMyAvatar(""));
    if (cache.current) URL.revokeObjectURL(cache.current.url);
    cache.current = { version, url };
    return url;
  } catch {
    return cache.current?.url ?? null;
  }
}

type LogoCache = Map<string, { version: string; url: string }>;

/**
 * As logos das empresas do `/me` (2026-10-16), uma por empresa COM logo.
 * O `/me` traz só a versão (md5); a imagem é baixada uma vez por versão e
 * entregue às telas como `blob:`. Empresa que perdeu a logo ou mudou de
 * versão libera a URL antiga. Falha ao baixar cai nas iniciais (ou na
 * versão anterior, se houver).
 */
async function loadCompanyLogos(
  companies: Array<{ id: string; logo_version?: string | null }>,
  cache: { current: LogoCache }
): Promise<Map<string, string>> {
  const next: LogoCache = new Map();
  await Promise.all(
    companies.map(async item => {
      const version = item.logo_version ?? null;
      if (!version) return;
      const cached = cache.current.get(item.id);
      if (cached?.version === version) {
        next.set(item.id, cached);
        return;
      }
      try {
        const url = URL.createObjectURL(await apiClient.getCompanyLogo("", item.id));
        next.set(item.id, { version, url });
      } catch {
        if (cached) next.set(item.id, cached);
      }
    })
  );
  for (const [id, entry] of cache.current) {
    if (next.get(id)?.url !== entry.url) URL.revokeObjectURL(entry.url);
  }
  cache.current = next;
  return new Map([...next].map(([id, entry]) => [id, entry.url]));
}

function extractCompanyId(pathname: string): string | null {
  const match = /^\/empresas\/([^/]+)/.exec(pathname);
  if (!match) return null;
  // Ignore reserved catalog route /empresas
  return match[1] === "page" ? null : match[1];
}

// O Context, o tipo do value e os hooks (`useAppShell`, `useSelectCompany`,
// `useRefreshProfileAfterCompanyCreated`) moram em `app-shell-context.ts`:
// este arquivo exporta SÓ o componente, de propósito, pra o Fast Refresh do
// Vite trocá-lo a quente sem reexecutar o módulo (o porquê está lá).

export function AppShellProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [token, setToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  // Defaults to collapsed, but remembers whatever the user last chose —
  // read once on mount (client-only) so SSR/hydration always starts collapsed.
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [sidebarExpandedHydrated, setSidebarExpandedHydrated] = useState(false);
  // A menção aberta e COMO foi aberta: o par fica junto, então abrir por
  // outro caminho (sem opções) volta o drawer a só leitura.
  const [drawer, setDrawer] = useState<{ mention: Mention; options: OpenMentionOptions } | null>(null);
  const openMention = useCallback(
    (mention: Mention, options?: OpenMentionOptions) => setDrawer({ mention, options: options ?? {} }),
    []
  );
  const [mentionChange, setMentionChange] = useState(0);
  // Qual menção o drawer mudou junto com a subida do contador; `null`
  // quando a mudança não é de uma menção só (vincular/mover).
  const [mentionChangeId, setMentionChangeId] = useState<number | null>(null);
  const reportMentionChange = useCallback((mentionId?: number) => {
    setMentionChangeId(mentionId ?? null);
    setMentionChange(count => count + 1);
  }, []);
  // Só a contagem da sidebar — lista de menção e de upload são de quem
  // as exibe (ver `refreshMentionCount` abaixo).
  const [mentionsTotal, setMentionsTotal] = useState(0);
  const sessionBootstrapStarted = useRef(false);
  // O modo de login da instalação (`"google"` ou nulo). Ref pra o perfil e
  // os botões lerem o valor sem depender de render; estado pra tela.
  const loginModeRef = useRef<string | null>(null);
  // A foto de perfil já baixada (2026-10-15): a rota exige o token, então
  // a casca a busca uma vez por VERSÃO (`avatar_updated_on`) e a entrega
  // às telas como `blob:`. Releitura do perfil com a mesma versão não
  // baixa de novo; versão nova solta o endereço anterior.
  const avatarRef = useRef<{ version: string; url: string } | null>(null);
  const logoCacheRef = useRef<LogoCache>(new Map());
  const [loginMode, setLoginMode] = useState<string | null>(null);

  const companyId = extractCompanyId(pathname);
  const company = companyId ? profile?.companies.find(item => item.id === companyId) ?? null : null;

  // Modo dev: o client manda `X-Company-Id` por requisição — a empresa
  // ativa (da URL) precisa chegar até ele. O servidor VALIDA o vínculo,
  // então isso não afrouxa escopo nenhum.
  //
  // **Durante o RENDER, não num efeito** (2026-08-30). Efeito de filho
  // roda ANTES do efeito do pai, então o primeiro fetch de cada tela
  // saía sem o header e tomava 401 — em toda entrada direta (F5 numa
  // rota de empresa, ou vinda da Home). O `CatalogImportPanel` chegou a
  // remendar isso localmente; as outras telas não tinham remendo.
  //
  // Render de pai acontece ANTES do render dos filhos, então publicar
  // aqui garante que o valor já está lá quando qualquer filho monta. A
  // atribuição é idempotente (mesma entrada, mesmo valor), que é o que
  // a torna segura sob StrictMode e re-render.
  setActiveCompanyId(company?.id ?? null);

  const landOnDefaultRoute = useCallback(
    (current: UserProfile) => {
      // Só as ATIVAS contam: o Máster recebe também as
      // desativadas, e uma delas não é "a única empresa" de ninguém.
      const selectable = activeCompanies(current.companies);
      if (!isMaster(current) && selectable.length === 1) {
        navigate(`/empresas/${selectable[0].id}/visao-geral`, { replace: true });
        return;
      }
      navigate("/", { replace: true });
    },
    [navigate]
  );

  useEffect(() => {
    if (!companyId) return;
    try {
      const stored = window.localStorage.getItem("reputation_recent_companies");
      const recent: string[] = stored ? JSON.parse(stored) : [];
      const updated = [companyId, ...recent.filter(id => id !== companyId)].slice(0, 6);
      window.localStorage.setItem("reputation_recent_companies", JSON.stringify(updated));
    } catch {
      // ignore
    }
  }, [companyId]);

  const bootstrapDevSession = useCallback(async () => {
    // Modo dev (`VITE_DEV_USER`): sem tela de login — o perfil nasce do
    // `GET /api/me` REAL do web_api (as empresas do vínculo), com os
    // campos do contrato morto preenchidos por default até cada um
    // ganhar fonte de verdade. `master_admin` liberado de propósito:
    // em dev você quer ver a navegação inteira.
    try {
      const me = await apiClient.me("");
      const logos = await loadCompanyLogos(me.companies, logoCacheRef);
      setToken(devUserId as string);
      setProfile({
        id: me.user_id,
        email: `${devUserId}@dev.local`,
        name: devUserId as string,
        // O modo dev entra como Máster — o topo do modelo de 4
        // papéis. Era `master_admin`, do dialeto órfão que saiu em
        // 2026-08-29 (ver `shared/utils/roles.ts`).
        roles: ["master"],
        companies: me.companies.map(item => ({
          id: item.id,
          name: item.name,
          // Vêm do servidor DE VERDADE, ao contrário dos defaults
          // abaixo — decidem se as telas mostram CENA e quantas
          // polaridades o editor oferece. Os campos são obrigatórios em
          // `CompanyContext` justamente pra este mapeamento não poder
          // esquecê-los em silêncio.
          displays_cena: item.displays_cena,
          polarity_scale: item.polarity_scale,
          is_active: item.is_active,
          groups: item.groups ?? [],
          slug: "",
          website: null,
          logo_url: logos.get(item.id) ?? null,
          permissions: []
        })),
        has_password: true,
        // A foto vem do servidor também no modo dev: estava fixa em
        // `null`, e a foto enviada pela tela de Perfil era gravada mas
        // nunca aparecia (o `refreshProfile` do dev cai aqui).
        avatar_url: await loadAvatar(me.avatar_updated_on ?? null, avatarRef),
        google_linked: false,
        mfa_enabled: false,
        created_at: new Date().toISOString()
      } as UserProfile);
    } catch {
      // Duas causas produzem exatamente esta falha, e a segunda já
      // custou uma caça ao erro errado: a API fora do ar, OU a API de pé
      // recusando a origem no CORS (abrir o site por `127.0.0.1` quando
      // só `localhost` está autorizado — o navegador bloqueia e o fetch
      // rejeita igualzinho a "servidor fora"). Citar as duas.
      setAuthError(
        `Modo dev: não consegui falar com o web_api em ${API_URL}. ` +
        "Duas causas possíveis: (1) ele não está de pé — rode `dev.cmd up`; " +
        "(2) ele está de pé mas bloqueou a origem no CORS — confira se você " +
        `abriu o site em ${window.location.origin} e se esse endereço está em ` +
        "`CORS_ALLOWED_ORIGINS` (devstack/dev.env)."
      );
    } finally {
      setBootstrapping(false);
    }
  }, []);

  /**
   * O perfil, montado a partir das DUAS fontes que existem agora.
   *
   * Nome, e-mail e método de login vêm do CADASTRO local, pelo
   * `GET /api/me` (2026-10-14: o nome que vale é o do convite, não o que
   * o Google põe no token); o `id_token` só cobre quem não tem cadastro
   * (stub). Papéis e empresas também vêm do `/me`.
   *
   * Os campos do contrato morto (`has_password`, `google_linked`,
   * `mfa_enabled`) continuam no tipo e nascem em `false`: quem os
   * exibia eram as telas de conta, que agora apontam pro Account
   * Console do Keycloak. Somem quando o tipo for reescrito.
   */
  const buildProfile = useCallback(async (idToken: string | null): Promise<UserProfile> => {
    const claims = idToken ? readIdentityClaims(idToken) : null;
    // Token vazio de propósito: quem o fornece é a sessão, que já foi
    // preenchida antes desta chamada (`applyAuth` cai nela). Passar o
    // access token aqui obrigaria a enfiá-lo na assinatura só pra ele
    // ser reenviado ao mesmo singleton.
    const me = await apiClient.me("");
    const logos = await loadCompanyLogos(me.companies, logoCacheRef);
    const avatarUrl = await loadAvatar(me.avatar_updated_on ?? null, avatarRef);
    return {
      id: me.user_id,
      email: me.email ?? claims?.email ?? "",
      name: me.name ?? claims?.name ?? claims?.email ?? me.user_id,
      // A role vem do access token validado pelo backend. O id_token
      // continua sendo usado apenas para nome e e-mail de exibição.
      roles: me.roles,
      companies: me.companies.map(item => ({
        id: item.id,
        name: item.name,
        displays_cena: item.displays_cena,
        polarity_scale: item.polarity_scale,
        is_active: item.is_active,
        groups: item.groups ?? [],
        slug: "",
        website: null,
        logo_url: logos.get(item.id) ?? null,
        permissions: []
      })),
      has_password: false,
      avatar_url: avatarUrl,
      // Desde 2026-10-07 o login pode ser pela conta Google
      // (`KEYCLOAK_LOGIN_PROVIDER`): o campo deixou de ser morto e diz se
      // ESTA sessão veio de lá — a tela de Segurança muda o texto por ele.
      google_linked: me.login_provider === "google",
      mfa_enabled: false,
      created_at: null,
      login_mode: loginModeRef.current,
      login_method: me.login_method ?? null
    } as UserProfile;
  }, []);

  /**
   * Bootstrap da sessão pelo Keycloak, em três desfechos.
   *
   * 1. Voltamos do Keycloak com um código → troca por tokens e pronto.
   * 2. Não temos sessão e ainda não tentamos → tentativa SILENCIOSA
   *    (`prompt=none`): com o cookie de SSO valendo, o Keycloak devolve o
   *    código sem desenhar tela (recarregar continua sem pedir senha);
   *    sem cookie, volta `login_required`.
   * 3. Já tentamos e não deu → a NOSSA tela de entrada, e o Google só
   *    abre quando a pessoa clica em "Entrar com a conta Google".
   *
   * **A tela de entrada é a porta (decisão da dona, 2026-10-15).** Até
   * então o passo 2 era um redirect DIRETO, com `kc_idp_hint`, que pulava
   * a tela daqui e jogava a pessoa no Google sem ela ter pedido — a tela
   * só aparecia depois de um erro. Custa uma ida e volta silenciosa ao
   * Keycloak a mais, que quem tem sessão nem percebe.
   *
   * **Entrada automática pelo Google (2026-10-21, pedido dos usuários).**
   * Quem já entrou pelo Google neste navegador e não saiu tem o e-mail
   * lembrado (`rememberedLoginHint`), e o passo 2 leva `kc_idp_hint` e
   * `login_hint`: o Keycloak repassa o `prompt=none` ao Google, que
   * devolve a conta SEM TELA se a sessão dele estiver aberta. Se o Google
   * precisaria mostrar algo, a recusa volta e cai no passo 3, como antes —
   * a porta continua sendo esta tela sempre que haveria uma tela.
   *
   * O terceiro passo depende da marca em `sessionStorage`: sem ela, quem
   * volta do Keycloak com erro, ou aperta Voltar no navegador, seria
   * mandado pra lá de novo, pra sempre.
   */
  const bootstrapOidcSession = useCallback(async () => {
    let handlingCallback = false;
    try {
      const mode = await fetchLoginMode();
      loginModeRef.current = mode;
      setLoginMode(mode);

      if (isAuthorizationResponse()) {
        handlingCallback = true;
        const tokens = await completeLogin();
        // A URL guarda `code` e `state` usados; deixá-los ali faz um F5
        // reenviar um código que o Keycloak já queimou. Desde 2026-10-11
        // o deep link sobrevive ao login: `beginLogin` guardou o endereço
        // de origem e é pra ele que voltamos, em vez de cair em `/`.
        const returnTo = consumeReturnPath();
        window.history.replaceState(null, "", returnTo ?? "/");
        forgetSilentLoginAttempt();
        setSession(tokens);
        const current = await buildProfile(tokens.idToken);
        // Quem entrou pelo Google fica lembrado neste navegador pra
        // entrada automática; quem entrou por senha, não. O e-mail é o do
        // token (o da conta Google), e não o do cadastro, porque é pro
        // Google que ele vai.
        const tokenEmail = tokens.idToken ? readIdentityClaims(tokens.idToken)?.email : null;
        const googleEmail = current.google_linked ? (tokenEmail ?? current.email) : null;
        if (googleEmail) rememberLoginHint(googleEmail);
        else forgetLoginHint();
        setToken(tokens.accessToken);
        setProfile(current);
        setBootstrapping(false);
        if (returnTo) navigate(returnTo, { replace: true });
        else landOnDefaultRoute(current);
        return;
      }

      // Volta do "Entrar com outra conta": a sessão do Keycloak acabou de
      // ser encerrada, e o login vai com `prompt=select_account` pro
      // Google mostrar a escolha de conta. Sem o login silencioso, que
      // só daria `login_required`.
      if (consumeSwitchAccount()) {
        markSilentLoginTried();
        const viaProvider = Boolean(mode) && !prefersPassword();
        await beginLogin({ idpHint: viaProvider ? mode : null, selectAccount: viaProvider });
        return;
      }

      if (!silentLoginAlreadyTried()) {
        markSilentLoginTried();
        // Com e-mail lembrado, o `prompt=none` vai até o Google (ver o
        // cabeçalho). Depende de "Accepts prompt=none forward from client"
        // no provedor do realm: desligado, o Keycloak responde
        // `login_required` sem ir ao Google, e tudo fica como antes.
        const loginHint = mode && !prefersPassword() ? rememberedLoginHint() : null;
        await beginLogin(loginHint ? { silent: true, idpHint: mode, loginHint } : { silent: true });
        return; // a página está saindo; nada depois disto roda
      }

      setBootstrapping(false);
    } catch (error) {
      // A volta do Keycloak com erro (o `login_required` da tentativa
      // silenciosa) deixava a URL em `/auth/callback?error=…`: o endereço
      // de origem volta pra barra, e o clique em "Entrar" o guarda de
      // novo — o deep link sobrevive à tela de entrada.
      if (handlingCallback) {
        window.history.replaceState(null, "", consumeReturnPath() ?? "/");
      }
      // `login_required` (e as recusas do Google à entrada automática) é
      // o desfecho ESPERADO de quem não tem sessão — vira tela de login
      // sem aviso de erro. Qualquer outra coisa é falha de verdade e
      // merece texto.
      if (!isSilentLoginRefusal(error)) {
        setAuthError(
          errorMessage(error, "Não foi possível entrar.")
        );
      }
      setBootstrapping(false);
    }
  }, [buildProfile, landOnDefaultRoute, navigate]);

  // Modo claro/escuro: o `index.html` já aplicou antes da pintura; aqui
  // reaplica (cobre o dev sem o script) e, no modo "sistema", acompanha
  // a troca do sistema operacional com a tela aberta.
  useEffect(() => {
    applyColorMode(readColorMode());
    return followSystemColorMode();
  }, []);

  useEffect(() => {
    const savedTheme = readThemeId();
    if (savedTheme) applyTheme(savedTheme);
    // Domínio de polaridade — global da instalação, rota sem
    // autenticação; carrega em paralelo ao bootstrap de sessão e falha
    // sem quebrar nada (a UI degrada pro nome de wire cru).
    void loadPolarityDomain();

    // O bootstrap de sessão roda UMA vez por montagem real. O StrictMode
    // de dev executa este efeito duas vezes, e as duas eram visíveis: na
    // ida, a segunda via a marca de "já tentou" e desenhava a tela de
    // login enquanto a página ainda saía pro Keycloak; na volta, a
    // segunda achava `state`/`verifier` já consumidos e mostrava "O
    // login expirou nesta aba" até a primeira terminar e redirecionar.
    // A ref sobrevive à remontagem simulada, por isso segura.
    if (sessionBootstrapStarted.current) return;
    sessionBootstrapStarted.current = true;

    if (devAuthEnabled) {
      void bootstrapDevSession();
      return;
    }
    if (oidcEnabled) {
      void bootstrapOidcSession();
      return;
    }
    // Nem stub de dev nem Keycloak: não há como autenticar. A tela diz
    // isso, em vez de oferecer o formulário do backend descartado.
    setBootstrapping(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * A renovação de token é assíncrona e mora fora do React — quando ela
   * troca o par, o `token` do contexto precisa acompanhar, senão as
   * telas seguem usando o vencido até o próximo render por outro
   * motivo.
   */
  useEffect(() => subscribeToSession(tokens => setToken(tokens?.accessToken ?? null)), []);

  const revokedIdToken = useRef<string | null>(null);

  /** Conta inativada (ou sem cargo) com a pessoa no meio do uso: daí em
   *  diante TODA chamada volta 403 `no_access`. Em vez de cada tela
   *  mostrar o mesmo erro solto, encerra a sessão local e leva à tela de
   *  login com a mensagem do servidor ("Você não tem acesso…"). Não
   *  encerra no Keycloak: o redirecionamento de saída engoliria a
   *  mensagem, e o cookie de SSO sozinho não dá acesso a nada. */
  useEffect(
    () =>
      onAccessRevoked(message => {
        // O `id_token` sobrevive à limpeza: "Entrar com outra conta"
        // precisa dele pra encerrar a sessão no Keycloak sem a tela de
        // confirmação de saída.
        revokedIdToken.current = currentTokens()?.idToken ?? revokedIdToken.current;
        // Sem acesso, a entrada automática só traria a mesma recusa a
        // cada carregamento.
        forgetLoginHint();
        clearSession();
        setProfile(null);
        setToken(null);
        setAuthError(message);
      }),
    []
  );

  /** Sessão perdida de vez (refresh recusado): volta pro login em vez
   *  de deixar a tela dando 401 em tudo. */
  useEffect(() => {
    onSessionLost(() => {
      setProfile(null);
      setToken(null);
      forgetSilentLoginAttempt();
    });
  }, []);

  useEffect(() => {
    const stored = window.localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    if (stored !== null) setSidebarExpanded(stored === "1");
    setSidebarExpandedHydrated(true);
  }, []);

  useEffect(() => {
    if (!sidebarExpandedHydrated) return;
    window.localStorage.setItem(SIDEBAR_EXPANDED_KEY, sidebarExpanded ? "1" : "0");
  }, [sidebarExpanded, sidebarExpandedHydrated]);

  // O shell não busca mais dado de tela: Uploads, Menções e Visão Geral
  // buscam os próprios (com polling onde faz sentido). Aqui sobra só a
  // CONTAGEM da sidebar — pedida com `size=1`, porque o que importa é o
  // `total` do envelope, não a lista.
  // Até quando o servidor pediu pra esperar (429). Este é o polling que
  // roda em TODA rota de empresa, então era o que mais batia depois de
  // um 429: a cada 15 s, em toda aba aberta, pra um número no menu
  // (2026-10-25, achado 25 da revisão de 2026-10-24).
  const [mentionCountPausedUntil, setMentionCountPausedUntil] = useState<
    number | null
  >(null);
  const refreshMentionCount = useCallback(async () => {
    if (!token || !company) return;
    try {
      // `/mentions/count` sem filtro, e não `listMentions({ size: 1 })`
      // com total (2026-10-26): o servidor guarda esse count por 3 min
      // (`mention_count_cache`), então 200 abas deixam de custar 13
      // contagens do recorte inteiro por segundo. O número é o mesmo —
      // todas as menções da empresa, não descartadas.
      const response = await apiClient.countMentions(token, {});
      // O recorte nu nunca tem orçamento, então o total sempre vem; o
      // `null` do tipo é da busca de texto, que não passa por aqui.
      if (response.total !== null) setMentionsTotal(response.total);
    } catch (failure) {
      // Contagem na sidebar não vale derrubar tela nenhuma — mas um 429
      // é pedido pra esperar, e o polling respeita.
      setMentionCountPausedUntil(retryPauseFrom(failure));
    }
  }, [company, token]);

  useEffect(() => {
    if (!company || !companyId) {
      setMentionsTotal(0);
      return;
    }
    void refreshMentionCount();
  }, [company?.id, companyId, token, refreshMentionCount]);

  // A contagem acompanha a esteira sem depender de qual tela está aberta
  // — mais lento que o polling das telas de propósito, porque aqui é só
  // um número no menu. É o ÚNICO polling que roda em qualquer rota de
  // empresa, então é o que mais ganha em pausar com a aba escondida:
  // antes uma aba esquecida batia no servidor indefinidamente.
  usePolling({
    enabled: Boolean(company && companyId),
    onTick: () => void refreshMentionCount(),
    intervalMs: MENTION_COUNT_INTERVAL_MS,
    pausedUntil: mentionCountPausedUntil,
    signature: String(mentionsTotal)
  });

  /**
   * Manda pro Keycloak, agora com tela (sem `prompt=none`).
   *
   * É o único caminho de login que sobrou. O formulário de e-mail/senha,
   * o desafio de MFA e o botão do Google falavam com o backend
   * DESCARTADO — não eram código morto, eram código vivo chamando rotas
   * que não existem, e o sintoma era um erro de rede na cara de quem
   * tentasse entrar. Senha, segundo fator e conta do Google passam a ser
   * assunto do Keycloak, que é onde a identidade mora desde 2026-07-30.
   */
  const login = useCallback(async (withPassword = false) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      forgetSilentLoginAttempt();
      const mode = loginModeRef.current;
      rememberPasswordPreference(withPassword);
      // Com o login por pessoa ligado, o clique PEDE login de novo: a
      // recusa que trouxe a pessoa até aqui costuma ser uma sessão de SSO
      // do jeito errado (senha em outro sistema da FSB), e reaproveitá-la
      // a devolveria à mesma recusa. Pelo Google, com `max_age=0` e a
      // escolha de conta, e não `prompt=login` (2026-10-21): sem `Prompt`
      // fixo no provedor do realm, o `prompt` do client chega ao Google,
      // e `login` lá poderia pedir a senha do Google a cada clique.
      await beginLogin(
        mode && !withPassword
          ? { idpHint: mode, selectAccount: true, reauthenticate: true }
          : { idpHint: null, forceLogin: Boolean(mode) }
      );
    } catch (error) {
      setAuthError(errorMessage(error, "Não foi possível entrar."));
      setAuthLoading(false);
    }
  }, []);

  /** "Entrar com outra conta" (2026-10-15): encerra a sessão no Keycloak
   *  e volta abrindo a escolha de conta do Google — ver `switchAccount`.
   *  É a saída de quem foi recusado com a conta errada. */
  const loginWithAnotherAccount = useCallback(async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const idToken = currentTokens()?.idToken ?? revokedIdToken.current;
      revokedIdToken.current = null;
      clearSession();
      await switchAccount(idToken);
    } catch (error) {
      setAuthError(errorMessage(error, "Não foi possível trocar de conta."));
      setAuthLoading(false);
    }
  }, []);

  // `useCallback` não é enfeite aqui: `logout` entra no value do
  // contexto, e função nova a cada render faria o `useMemo` do value
  // (lá embaixo) nunca segurar nada.
  const logout = useCallback(() => {
    if (devAuthEnabled) {
      // Sem sessão pra encerrar no modo dev — sair derrubaria numa tela
      // de login que não tem o que fazer.
      navigate("/", { replace: true });
      return;
    }
    const idToken = currentTokens()?.idToken ?? null;
    const accessToken = currentTokens()?.accessToken ?? null;
    setToken(null);
    setProfile(null);
    setMentionsTotal(0);
    clearSession();
    if (oidcEnabled) {
      // Primeiro a API (2026-10-20): o token desta aba deixa de valer lá
      // mesmo se o redirect abaixo não chegar ao fim. Com teto de tempo e
      // sem bloquear a saída se falhar — sair nunca pode depender disso.
      // Depois encerra NO KEYCLOAK, não só aqui. Limpar o estado local
      // sozinho seria um "sair" que não sai: o cookie de SSO continuaria
      // valendo e o `prompt=none` do próximo carregamento traria a pessoa
      // de volta logada — o pior desfecho pra quem clicou em Sair num
      // computador compartilhado.
      void revokeThenEndSession(accessToken, idToken);
      return;
    }
    navigate("/", { replace: true });
  }, [devAuthEnabled, navigate]);

  const refreshProfile = useCallback(async () => {
    if (devAuthEnabled) {
      await bootstrapDevSession();
      return;
    }
    if (!token) return;
    // `apiClient.profile` chama `/auth/me`, rota do backend DESCARTADO
    // — no modo Keycloak isso tomava 404 e a promessa rejeitava. O
    // efeito era visível: salvar `displays_cena` ou a escala em Ajustes,
    // e criar empresa, só apareciam no shell depois de um F5.
    // `buildProfile` é o mesmo caminho do bootstrap (`/me` + claims do
    // id token), que é o que existe de verdade.
    const current = await buildProfile(currentTokens()?.idToken ?? null);
    setProfile(current);
  }, [token, bootstrapDevSession, buildProfile]);

  // Um Máster pode desativar uma empresa enquanto outra pessoa a
  // tem aberta: a primeira chamada seguinte, de QUALQUER tela, volta o
  // 403 com `code: company_disabled` (nunca se lê o texto da mensagem).
  // Aqui a casca leva a pessoa de volta ao hub com o aviso e relê o
  // `/me`, que já não traz mais a empresa. `disabledHandling` junta as várias
  // chamadas em voo que voltam o mesmo 403 de uma vez só.
  const disabledHandling = useRef(false);
  useEffect(
    () =>
      onCompanyDisabled(() => {
        if (disabledHandling.current) return;
        disabledHandling.current = true;
        navigate("/empresas", { replace: true, state: { notice: COMPANY_DISABLED_NOTICE } });
        void refreshProfile().finally(() => {
          disabledHandling.current = false;
        });
      }),
    [navigate, refreshProfile]
  );

  // `updateMention`/`reclassifyMentions` saíram em 2026-08-04: o drawer
  // é read-only até a Camada 2.5 existir, então não há atualização
  // otimista de menção pra propagar.

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  // O value do contexto PRECISA ser memoizado, e precisa ser aqui em
  // cima — hook não pode ficar depois dos early returns abaixo.
  //
  // Sem isso, o objeto era novo a cada render deste provider, e como o
  // contador da sidebar faz polling de 15s, TODO consumidor de
  // `useAppShell` no app inteiro re-renderizava a cada 15 segundos —
  // inclusive quando o número voltava igual, que é o caso comum. Com o
  // memo, poll que não muda nada não custa render nenhum.
  //
  // `refreshProfile` e `logout` são `useCallback` justamente pra
  // sustentar isso: uma função nova por render aqui zeraria o ganho.
  const value = useMemo<AppShellContextValue | null>(
    () =>
      token && profile
        ? {
            token,
            profile,
            company,
            mentionsTotal,
            openMention,
            mentionChange,
            mentionChangeId,
            refreshProfile,
            logout
          }
        : null,
    [token, profile, company, mentionsTotal, mentionChange, refreshProfile, logout, openMention, mentionChangeId]
  );

  if (bootstrapping) {
    return <main className="app-loading"><LoaderCircle className="spin" size={28}/><span>Carregando sessão…</span></main>;
  }

  if (!token || !profile || !value) {
    return (
      <LoginScreen
        loading={authLoading}
        error={authError}
        // `null` quando não há Keycloak: a tela explica em vez de
        // oferecer um botão que não leva a lugar nenhum.
        onLogin={oidcEnabled ? () => void login() : null}
        onSwitchAccount={oidcEnabled ? () => void loginWithAnotherAccount() : null}
        loginProvider={loginMode}
        onLoginWithPassword={oidcEnabled && loginMode ? () => void login(true) : null}
      />
    );
  }

  if (profile.companies.length === 0 && !isMaster(profile)) {
    return (
      <main className="app-loading">
        <ShieldCheck size={30}/><strong>Nenhuma empresa vinculada</strong>
        <span>Solicite acesso a uma empresa para usar a plataforma.</span>
        <button className="secondary-button" onClick={logout}>Sair</button>
      </main>
    );
  }

  return (
    <AppShellContext.Provider value={value}>
      <CopilotoProvider token={token}>
        <div className={`app-shell ${sidebarExpanded ? "sidebar-is-expanded" : ""}`}>
          <a className="skip-link" href="#main-content">Ir para o conteúdo principal</a>
          <Sidebar
            pathname={pathname}
            search={search}
            company={company}
            open={menu}
            close={() => setMenu(false)}
            expanded={sidebarExpanded}
            toggleExpanded={() => setSidebarExpanded(value2 => !value2)}
            profile={profile}
            mentionCount={mentionsTotal}
            logout={logout}
          />
          <main className="main" id="main-content">
            <Topbar
              title={pageTitleForPath(pathname, company, search)}
              back={backTargetForPath(pathname, search)}
              token={token}
              company={company}
              onMenu={() => setMenu(true)}
              copiloto={canOperate(profile)}
            />
            {/* O filtro de menções é compartilhado com o relatório e é POR
                EMPRESA: o `key` remonta a loja ao trocar de empresa, e é
                isso que lê o storage da nova (ver
                `mention-filter-store.tsx`). */}
            <MentionFilterProvider key={companyId ?? "none"} companyId={companyId}>
              <div className="content" ref={contentRef}>{children}</div>
            </MentionFilterProvider>
          </main>
          <KeyboardShortcuts
            company={company}
            profile={profile}
            toggleSidebar={() => setSidebarExpanded(value2 => !value2)}
          />
          {drawer && company && (
            <MentionDrawer
              mention={drawer.mention}
              loadCatalog={drawer.options.loadCatalog}
              token={token}
              readOnly={!canOperate(profile)}
              close={() => setDrawer(null)}
              onChanged={reportMentionChange}
            />
          )}
        </div>
      </CopilotoProvider>
    </AppShellContext.Provider>
  );
}
